import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";
import { Ambiguity, ComplexLanguage, Inconsistency } from "@/types/review.types";
import { useFuzzySearch } from "./useFuzzySearch";

export interface UseTileReplaceOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

const extractReplacementTexts = (
  tile: ReviewTileItem
): { findText: string; replaceText: string } | null => {
  const originalData = tile.originalData;
  if (originalData == null) return null;

  if (tile.originalKey === "ambiguities") {
    const data = originalData as Ambiguity;
    if (data.ambiguousText && data.suggestedclause) {
      return { findText: data.ambiguousText, replaceText: data.suggestedclause };
    }
  } else if (tile.originalKey === "complexLanguage") {
    const data = originalData as ComplexLanguage;
    if (data.complexText && data.suggestions) {
      return { findText: data.complexText, replaceText: data.suggestions };
    }
  } else if (tile.originalKey === "inconsistencies") {
    const data = originalData as Inconsistency;
    if (data.text1 && data.suggestion) {
      return { findText: data.text1, replaceText: data.suggestion };
    }
  }

  return null;
};

export const useTileReplace = (options?: UseTileReplaceOptions) => {
  const [isReplacing, setIsReplacing] = useState(false);
  const [replacingTileId, setReplacingTileId] = useState<string | null>(null);
  const { fuzzySearchInDocument } = useFuzzySearch();

  const replaceSingleTile = async (tile: ReviewTileItem): Promise<boolean> => {
    setIsReplacing(true);
    setReplacingTileId(tile.id);

    const replacementTexts = extractReplacementTexts(tile);
    if (!replacementTexts) {
      options?.onError?.("This tile is not replaceable.");
      setIsReplacing(false);
      setReplacingTileId(null);
      return false;
    }

    const { findText, replaceText } = replacementTexts;
    // Truncate findText to 255 characters to avoid SearchStringInvalidOrTooLong error
    const truncatedFindText = findText.substring(0, 255);

    try {
      await Word.run(async (context) => {
        const searchOptions = { matchCase: false, matchWholeWord: false };
        const searchResults = context.document.body.search(truncatedFindText, searchOptions);
        searchResults.load("items");
        await context.sync();

        if (searchResults.items.length > 0) {
          // Replace all occurrences.
          searchResults.items.forEach((item) => {
            item.insertText(replaceText, Word.InsertLocation.replace);
          });
          await context.sync();
          options?.onSuccess?.(`Replaced ${searchResults.items.length} instance(s).`);
        } else {
          // Fallback to fuzzy search
          const fuzzyMatchFound = await fuzzySearchInDocument(truncatedFindText);

          if (fuzzyMatchFound) {
            // If fuzzy search found and selected the text, we need to replace the selection
            await Word.run(async (context) => {
              const selection = context.document.getSelection();
              selection.insertText(replaceText, Word.InsertLocation.replace);
              await context.sync();
            });
            options?.onSuccess?.("Replaced 1 instance using fuzzy search.");
          } else {
            options?.onError?.(`Could not find the text to replace.`);
          }
        }
      });
      return true;
    } catch (error: unknown) {
      console.error("Error replacing text:", error);
      const message = error instanceof Error ? error.message : "Unknown error";
      options?.onError?.(`Failed to replace text: ${message}`);
      return false;
    } finally {
      setIsReplacing(false);
      setReplacingTileId(null);
    }
  };

  return {
    isReplacing,
    replacingTileId,
    replaceSingleTile,
  };
};
